import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FiX, FiUserPlus, FiClock } from 'react-icons/fi';
import WorkspaceDialog from './WorkspaceDialog';
import { useTranslation } from 'react-i18next';
import { BACKEND_MAIN_URL } from '../config';

/**
 * Manage who can work inside a project (#72).
 *
 * Only the owner can change membership; everyone else sees a read-only roster.
 * That is enforced server-side too — this just avoids showing controls that
 * would 403.
 *
 * Invites are by email and need no outbound mail: if the address already has
 * an account the project appears for them immediately, otherwise the invite
 * waits and is claimed the first time they sign in.
 */

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'X-User-ID': localStorage.getItem('userId') || '',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const ROLE_ORDER = { owner: 0, editor: 1, viewer: 2 };

const ProjectShareModal = ({ project, onClose }) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [yourRole, setYourRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const projectId = project?.id;

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const resp = await axios.get(
        `${BACKEND_MAIN_URL}/api/projects/${projectId}/members`,
        { headers: authHeaders() },
      );
      const list = resp.data?.members || [];
      list.sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9));
      setMembers(list);
      setYourRole(resp.data?.your_role || null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error
        || 'share.load_failed');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (!project) return null;

  const isOwner = yourRole === 'owner';

  const invite = async (e) => {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${BACKEND_MAIN_URL}/api/projects/${projectId}/members`,
        { email: address, role },
        { headers: authHeaders() },
      );
      setEmail('');
      // The invite exists either way; say whether the email actually went out
      // so the owner knows to tell the person themselves if it did not.
      setNotice(data?.member?.email_sent ? 'share.email_sent' : 'share.email_not_sent');
      await load();
    } catch (err) {
      setError(err.response?.data?.error
        || 'share.invite_failed');
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (member, nextRole) => {
    setBusy(true);
    try {
      await axios.patch(
        `${BACKEND_MAIN_URL}/api/projects/${projectId}/members/${member.user_id}`,
        { role: nextRole },
        { headers: authHeaders() },
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.error
        || 'share.role_failed');
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (member) => {
    const label = member.email || t('share.this_person', 'this person');
    if (!window.confirm(t('share.confirm_remove',
      'Remove {{who}} from this project? They will lose access to its files and chats.',
      { who: label }))) return;
    setBusy(true);
    try {
      if (member.pending) {
        await axios.delete(
          `${BACKEND_MAIN_URL}/api/projects/${projectId}/invites`,
          { headers: authHeaders(), params: { email: member.email } },
        );
      } else {
        await axios.delete(
          `${BACKEND_MAIN_URL}/api/projects/${projectId}/members/${member.user_id}`,
          { headers: authHeaders() },
        );
      }
      await load();
    } catch (err) {
      setError(err.response?.data?.error
        || 'share.remove_failed');
    } finally {
      setBusy(false);
    }
  };

  const roleLabel = (r) => ({
    owner: t('share.role_owner', 'Owner'),
    editor: t('share.role_editor', 'Editor'),
    viewer: t('share.role_viewer', 'Viewer'),
  }[r] || r);

  return <WorkspaceDialog title={t('share.title')} onClose={() => { if (!busy) onClose(); }} className="ws-share-dialog">
    <div className="ws-share-content">
      <p className="ws-muted">{t('share.subtitle')}</p>
      {isOwner && <form className="ws-share-invite" onSubmit={invite}>
        <label className="ws-field">{t('workspace:accountEmail')}<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t('share.email_placeholder')} disabled={busy} required /></label>
        <label className="ws-field">{t('workspace:accessRole')}<select value={role} onChange={(event) => setRole(event.target.value)} disabled={busy}>
          <option value="viewer">{roleLabel('viewer')}</option><option value="editor">{roleLabel('editor')}</option>
        </select></label>
        <button className="ws-primary" type="submit" disabled={busy || !email.trim()}><FiUserPlus />{t('share.invite')}</button>
      </form>}
      {isOwner && <p className="ws-field-hint">{t('share.role_hint')}</p>}
      {notice && <p role="status" className="ws-field-hint">{t(notice)}</p>}
      {error && <div role="alert" className="ws-error"><p>{t(error)}</p><button className="ws-text-button" onClick={load} disabled={busy}>{t('workspace:retry')}</button></div>}
      {loading ? <p role="status">{t('workspace:loading')}</p> : <ul className="ws-share-members">
        {members.map((member) => <li key={member.id || member.email || member.user_id}>
          <div className="ws-share-person"><strong>{member.name || member.email || member.user_id}</strong>
            {member.name && member.email && <small>{member.email}</small>}
            {member.pending && <small className="ws-share-pending"><FiClock />{t('share.pending')}</small>}
          </div>
          <div className="ws-share-member-actions">
            {isOwner && member.role !== 'owner' && !member.pending ? <select aria-label={`${t('workspace:accessRole')} — ${member.name || member.email}`} value={member.role} disabled={busy} onChange={(event) => changeRole(member, event.target.value)}>
              <option value="viewer">{roleLabel('viewer')}</option><option value="editor">{roleLabel('editor')}</option>
            </select> : <span className="ws-share-role">{roleLabel(member.role)}</span>}
            {isOwner && member.role !== 'owner' && <button className="ws-icon-button" type="button" onClick={() => removeMember(member)} disabled={busy} aria-label={`${t('share.remove')} ${member.name || member.email}`}><FiX /></button>}
          </div>
        </li>)}
      </ul>}
      {!isOwner && !loading && <p className="ws-field-hint">{t('share.not_owner')}</p>}
    </div>
  </WorkspaceDialog>;
};

export default ProjectShareModal;
